import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { Link, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiLayers,
  FiSettings,
  FiList,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiCopy,
  FiCheck,
  FiCheckCircle,
  FiXCircle,
  FiX,
  FiClock,
  FiAlertTriangle,
  FiPlus,
  FiTrash2,
  FiTarget,
  FiCalendar,
  FiAward,
  FiZap,
} from "react-icons/fi";
import {
  getBoomCardsSettings,
  updateBoomCardsSettings,
  getBoomCardsHistory,
} from "../api/tapGameApi";
import { getEvents } from "../api/eventApi";
import GameAnalyticsCharts from "./components/GameAnalyticsCharts";
import dayjs from "dayjs";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateStr);
  }
};

export default function BoomCards() {
  const navigate = useNavigate();

  // Navigation back handler
  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/games");
    }
  };

  // Active Tab: "settings" or "history"
  const [activeTab, setActiveTab] = useState("settings");

  // Settings State
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [formSettings, setFormSettings] = useState({
    gameTime: 30,
    minPoints: 10,
    maxPoints: 50,
    cooldownSeconds: 180,
    isActive: true,
  });

  // Target Point Probability Buckets State
  const [pointProbabilities, setPointProbabilities] = useState([
    { min: 10, max: 20, percentage: 50 },
    { min: 21, max: 30, percentage: 30 },
    { min: 31, max: 40, percentage: 15 },
    { min: 41, max: 50, percentage: 5 },
  ]);

  // Event State: Default to active event
  const [eventsList, setEventsList] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");

  const [stats, setStats] = useState({
    todaySessions: 0,
    todayPoints: 0,
    eventTotalPoints: 0,
    eventTotalSessions: 0,
    totalSessions: 0,
    completedSessions: 0,
    totalPointsAwarded: 0,
  });

  // Daily Analytics Data for Visual Charts
  const [dailyStats, setDailyStats] = useState([]);
  const [analyticsHistory, setAnalyticsHistory] = useState([]);

  // History State
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    perPage: 20,
  });

  // UI state
  const [copiedSessionId, setCopiedSessionId] = useState("");
  const searchDebounceTimer = useRef(null);

  // Helper to compute Boom Cards specific metrics accurately
  const computeBoomCardsStats = (list = [], serverStats = null, currentDailyStats = [], prevStats = null) => {
    const todayStr = dayjs().format("YYYY-MM-DD");

    // 1. Check server-provided stats
    const serverTodaySessions = serverStats?.todaySessions;
    const serverTodayPoints = serverStats?.todayPoints;
    const serverEventPoints = Number(
      serverStats?.eventTotalPoints ?? serverStats?.truePointsAwarded ?? serverStats?.totalPointsAwarded ?? serverStats?.totalBoomCardsPointsAwarded ?? 0
    );
    const serverEventSessions = Number(
      serverStats?.eventTotalSessions ?? serverStats?.totalSessions ?? serverStats?.totalBoomCardsSessions ?? 0
    );

    // 2. Client fallback for today's stats if server did not calculate them
    let todaySessionsCalc = 0;
    let todayPointsCalc = 0;

    const sourceDaily = Array.isArray(currentDailyStats) && currentDailyStats.length > 0 ? currentDailyStats : [];
    const todayDailyRow = sourceDaily.find(
      (d) => d?.date === todayStr || dayjs(d?.date).format("YYYY-MM-DD") === todayStr
    );

    if (todayDailyRow) {
      todaySessionsCalc = Number(todayDailyRow.sessions || todayDailyRow.count || 0);
      todayPointsCalc = Number(todayDailyRow.points || todayDailyRow.claimedPoints || 0);
    } else if (Array.isArray(list) && list.length > 0) {
      const todayList = list.filter((row) => {
        const d = row.createdAt || row.startedAt || row.completedAt;
        return d && dayjs(d).format("YYYY-MM-DD") === todayStr;
      });
      todaySessionsCalc = todayList.length;
      todayPointsCalc = todayList.reduce((sum, r) => {
        if (r.status === "completed" || r.status === "Claimed") {
          return sum + Number(r.claimedPoints ?? r.pointsAwarded ?? 0);
        }
        return sum;
      }, 0);
    }

    const completedList = list.filter((h) => h.status === "completed" || h.status === "Claimed");
    const listEventPoints = completedList.reduce(
      (acc, h) => acc + Number(h.claimedPoints ?? h.pointsAwarded ?? 0),
      0
    );

    return {
      todaySessions: serverTodaySessions != null ? Number(serverTodaySessions) : (prevStats?.todaySessions ?? todaySessionsCalc),
      todayPoints: serverTodayPoints != null ? Number(serverTodayPoints) : (prevStats?.todayPoints ?? todayPointsCalc),
      eventTotalPoints: serverEventPoints > 0 ? serverEventPoints : (listEventPoints > 0 ? listEventPoints : (prevStats?.eventTotalPoints ?? 0)),
      eventTotalSessions: serverEventSessions > 0 ? serverEventSessions : (list.length > 0 ? list.length : (prevStats?.eventTotalSessions ?? 0)),
      totalSessions: serverEventSessions > 0 ? serverEventSessions : (list.length > 0 ? list.length : (prevStats?.totalSessions ?? 0)),
      completedSessions: Number(serverStats?.completedSessions ?? completedList.length ?? prevStats?.completedSessions ?? 0),
      totalPointsAwarded: serverEventPoints > 0 ? serverEventPoints : (listEventPoints > 0 ? listEventPoints : (prevStats?.totalPointsAwarded ?? 0)),
    };
  };

  // Fetch Settings
  const fetchSettings = useCallback(async (showToast = false) => {
    setSettingsLoading(true);
    try {
      const res = await getBoomCardsSettings();
      if (res?.status && res?.data) {
        const d = res.data;
        setFormSettings({
          gameTime: Number(d.gameTime ?? 30),
          minPoints: Number(d.minPoints ?? 10),
          maxPoints: Number(d.maxPoints ?? 50),
          cooldownSeconds: Number(d.cooldownSeconds ?? 180),
          isActive: Boolean(d.isActive === 1 || d.isActive === true || d.isActive === "1"),
        });

        if (Array.isArray(d.pointProbabilities) && d.pointProbabilities.length > 0) {
          setPointProbabilities(
            d.pointProbabilities.map((b) => ({
              min: Number(b.min ?? 0),
              max: Number(b.max ?? 0),
              percentage: Number(b.percentage ?? 0),
            }))
          );
        } else {
          const minP = Number(d.minPoints ?? 10);
          const maxP = Number(d.maxPoints ?? 50);
          const span = Math.max(4, maxP - minP);
          const q1 = Math.round(minP + span * 0.25);
          const q2 = Math.round(minP + span * 0.5);
          const q3 = Math.round(minP + span * 0.75);
          setPointProbabilities([
            { min: minP, max: q1, percentage: 50 },
            { min: q1 + 1, max: q2, percentage: 30 },
            { min: q2 + 1, max: q3, percentage: 15 },
            { min: q3 + 1, max: maxP, percentage: 5 },
          ]);
        }

        if (d.stats && (!selectedEventId || selectedEventId === "all")) {
          setStats((prev) => computeBoomCardsStats([], d.stats, d.dailyStats || [], prev));
        }
        if (showToast) toast.success("Boom Cards settings reloaded!");
      }
    } catch (err) {
      console.warn("Failed to load Boom Cards settings, using defaults:", err.message);
      if (showToast) {
        toast.error(err.message || "Failed to load Boom Cards settings");
      }
    } finally {
      setSettingsLoading(false);
    }
  }, [selectedEventId]);

  // Fetch History
  const fetchHistory = useCallback(async (showToast = false, overrideEventId = null) => {
    setHistoryLoading(true);
    const activeEventFilter = overrideEventId !== null ? overrideEventId : selectedEventId;
    try {
      const res = await getBoomCardsHistory({
        page,
        perPage,
        search: debouncedSearch,
        status: statusFilter,
        eventId: activeEventFilter && activeEventFilter !== "all" ? activeEventFilter : undefined,
      });

      if (res?.status) {
        const historyList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.history)
            ? res.data.history
            : [];

        setHistory(historyList);
        setAnalyticsHistory(historyList);

        if (Array.isArray(res.dailyStats) && res.dailyStats.length > 0) {
          setDailyStats(res.dailyStats);
        }

        setStats((prev) => computeBoomCardsStats(historyList, res.stats || res.data?.stats, res.dailyStats || [], prev));

        const serverPagination = res.pagination || res.data?.pagination || {};
        const totalRecords =
          typeof serverPagination.total === "number"
            ? serverPagination.total
            : typeof res.total === "number"
              ? res.total
              : historyList.length;

        const totalPagesCount =
          typeof serverPagination.lastPage === "number"
            ? serverPagination.lastPage
            : typeof serverPagination.totalPages === "number"
              ? serverPagination.totalPages
              : Math.max(1, Math.ceil(totalRecords / perPage));

        const currentPageNum =
          typeof serverPagination.currentPage === "number"
            ? serverPagination.currentPage
            : typeof serverPagination.page === "number"
              ? serverPagination.page
              : page;

        setPagination({
          total: totalRecords,
          totalPages: totalPagesCount,
          page: currentPageNum,
          perPage,
        });

        if (showToast) toast.success("Boom Cards history reloaded!");
      }
    } catch (err) {
      console.warn("Failed to load Boom Cards history:", err.message);
      if (showToast) {
        toast.error(err.message || "Failed to load history");
      }
      setHistory([]);
      setStats({
        todaySessions: 0,
        todayPoints: 0,
        eventTotalPoints: 0,
        eventTotalSessions: 0,
        totalSessions: 0,
        completedSessions: 0,
        totalPointsAwarded: 0,
      });
    } finally {
      setHistoryLoading(false);
    }
  }, [page, perPage, debouncedSearch, statusFilter, selectedEventId]);

  // Initial Load - Fetch Events & set default running event
  useEffect(() => {
    let isMounted = true;
    getEvents({ limit: 100, page: 1, status: "all" })
      .then((res) => {
        if (!isMounted) return;
        const rawData = res?.data || res;
        const evList = Array.isArray(rawData?.events)
          ? rawData.events
          : Array.isArray(rawData)
          ? rawData
          : Array.isArray(res?.events)
          ? res.events
          : [];
        setEventsList(evList);

        const running = evList.find((e) => e.status?.toLowerCase() === "running");
        if (running) {
          const initialEventId = String(running.eventId || running.id);
          setSelectedEventId(initialEventId);
          fetchHistory(false, initialEventId);
        } else if (evList.length > 0) {
          const initialEventId = String(evList[0].eventId || evList[0].id);
          setSelectedEventId(initialEventId);
          fetchHistory(false, initialEventId);
        } else {
          setSelectedEventId("all");
          fetchHistory(false, "all");
        }
      })
      .catch((err) => {
        console.warn("Could not load events list:", err.message);
        if (isMounted) {
          setEventsList([]);
          setSelectedEventId("all");
          fetchHistory(false, "all");
        }
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch settings once on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Handle Event Selector Change
  const handleEventChange = (newEventId) => {
    setSelectedEventId(newEventId);
    setPage(1);
    fetchHistory(false, newEventId);
  };

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 350);
  };

  const handleCopySession = (sessionId) => {
    if (!sessionId) return;
    navigator.clipboard.writeText(sessionId);
    setCopiedSessionId(sessionId);
    toast.success("Session ID copied!");
    setTimeout(() => setCopiedSessionId(""), 2000);
  };

  // Bucket Manipulation Helpers
  const handleAddBucket = () => {
    const lastBucket = pointProbabilities[pointProbabilities.length - 1];
    const newMin = lastBucket ? Number(lastBucket.max) + 1 : Number(formSettings.minPoints);
    const newMax = Math.min(newMin + 10, Number(formSettings.maxPoints));
    setPointProbabilities([...pointProbabilities, { min: newMin, max: newMax, percentage: 0 }]);
  };

  const handleRemoveBucket = (index) => {
    if (pointProbabilities.length <= 1) {
      toast.error("At least one probability range bucket is required.");
      return;
    }
    setPointProbabilities(pointProbabilities.filter((_, i) => i !== index));
  };

  const handleBucketChange = (index, field, value) => {
    const updated = [...pointProbabilities];
    updated[index] = { ...updated[index], [field]: Number(value) };
    setPointProbabilities(updated);
  };

  const handleAutoBalance = () => {
    if (pointProbabilities.length === 0) return;
    const count = pointProbabilities.length;
    const base = Math.floor(100 / count);
    const rem = 100 - base * count;
    const balanced = pointProbabilities.map((b, i) => ({
      ...b,
      percentage: i === 0 ? base + rem : base,
    }));
    setPointProbabilities(balanced);
    toast.success("Boom Cards probabilities auto-balanced to 100%!");
  };

  const handleResetBuckets = () => {
    const minP = Number(formSettings.minPoints ?? 10);
    const maxP = Number(formSettings.maxPoints ?? 50);
    const span = Math.max(4, maxP - minP);
    const q1 = Math.round(minP + span * 0.25);
    const q2 = Math.round(minP + span * 0.5);
    const q3 = Math.round(minP + span * 0.75);
    setPointProbabilities([
      { min: minP, max: q1, percentage: 50 },
      { min: q1 + 1, max: q2, percentage: 30 },
      { min: q2 + 1, max: q3, percentage: 15 },
      { min: q3 + 1, max: maxP, percentage: 5 },
    ]);
    toast.success("Reset to recommended Boom Cards distribution!");
  };

  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();

    const gTime = Number(formSettings.gameTime);
    const minP = Number(formSettings.minPoints);
    const maxP = Number(formSettings.maxPoints);
    const cDown = Number(formSettings.cooldownSeconds);

    if (isNaN(gTime) || gTime < 5 || gTime > 600) {
      toast.error("Game time must be between 5 and 600 seconds.");
      return;
    }
    if (isNaN(minP) || minP < 1) {
      toast.error("Min points must be at least 1.");
      return;
    }
    if (isNaN(maxP) || maxP <= minP) {
      toast.error("Max points must be greater than min points.");
      return;
    }
    if (isNaN(cDown) || cDown < 0 || cDown > 86400) {
      toast.error("Cooldown must be between 0 and 86400 seconds.");
      return;
    }

    // Validate probability buckets
    const totalPercentage = pointProbabilities.reduce(
      (acc, b) => acc + (Number(b.percentage) || 0),
      0
    );
    if (pointProbabilities.length > 0 && totalPercentage !== 100) {
      toast.error(`Total probability must equal 100%! Current sum: ${totalPercentage}%`);
      return;
    }

    for (let i = 0; i < pointProbabilities.length; i++) {
      const b = pointProbabilities[i];
      if (Number(b.min) > Number(b.max)) {
        toast.error(`Bucket #${i + 1}: Min points (${b.min}) cannot exceed Max (${b.max})!`);
        return;
      }
    }

    setSavingSettings(true);
    try {
      const payload = {
        gameTime: gTime,
        minPoints: minP,
        maxPoints: maxP,
        cooldownSeconds: cDown,
        isActive: Boolean(formSettings.isActive),
        pointProbabilities,
      };

      const res = await updateBoomCardsSettings(payload);
      if (res?.status) {
        toast.success(res.message || "Boom Cards settings saved successfully!");
      }
    } catch (err) {
      console.error("Error saving Boom Cards settings:", err);
      toast.error(err.message || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Resolve current active event label
  const activeRunningEvent = useMemo(() => {
    if (!Array.isArray(eventsList)) return null;
    return eventsList.find((e) => e.status?.toLowerCase() === "running");
  }, [eventsList]);

  const selectedEventObj = useMemo(() => {
    if (!selectedEventId || selectedEventId === "all" || !Array.isArray(eventsList)) return null;
    return eventsList.find(
      (e) => String(e.eventId) === String(selectedEventId) || String(e.id) === String(selectedEventId)
    );
  }, [selectedEventId, eventsList]);

  const selectedEventName = selectedEventObj
    ? selectedEventObj.eventName || `Event #${selectedEventObj.id}`
    : selectedEventId === "all"
    ? "All Events"
    : "Active Event";

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        {/* Full-width container matching SlotsGame.js */}
        <div className="p-5 sm:p-7 space-y-5 w-full">
          {/* Breadcrumb and Back Button */}
          <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-1 hover:text-black transition cursor-pointer p-1 -ml-1 rounded hover:bg-gray-200"
              title="Go back"
            >
              <FiArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/dashboard" className="hover:text-black transition">
              Dashboard
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/games" className="hover:text-black transition flex items-center gap-1">
              <span>Games Management</span>
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <span className="text-amber-800 font-semibold">Boom Cards</span>
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center text-2xl shadow-xs">
                🃏
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-black text-gray-900">
                    Boom Cards Game
                  </h1>
                  <span className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-black uppercase px-2 py-0.5 rounded-full">
                    Live
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Manage Boom Cards game rules, reward range, timer, and play audit logs.
                </p>
              </div>
            </div>

            {/* Top Game Switcher & Refresh Button */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="flex items-center bg-gray-200/80 p-1 rounded-xl text-xs font-bold">
                <Link
                  to="/games"
                  className="px-2.5 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1"
                  title="All Games Priority & Overview"
                >
                  <FiLayers className="h-3.5 w-3.5" /> All Games
                </Link>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg bg-white text-black shadow-xs flex items-center gap-1.5"
                >
                  <span>🃏</span> Boom Cards
                </button>
                <Link
                  to="/tap-game"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🎮</span> Tap Game
                </Link>
                <Link
                  to="/slots-game"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🎰</span> 777 Slots
                </Link>
                <Link
                  to="/spin-wheel"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🎡</span> Spin Wheel
                </Link>
              </div>

              <button
                onClick={() => {
                  if (activeTab === "settings") fetchSettings(true);
                  else fetchHistory(true);
                }}
                disabled={settingsLoading || historyLoading}
                className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-2xs"
              >
                <FiRefreshCw
                  size={13}
                  className={settingsLoading || historyLoading ? "animate-spin text-amber-600" : ""}
                />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* 5 Core Items in Single Unified Row (Event Selector + 4 Key Metrics) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3.5">
            {/* 1. Event Selector Card */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5">
                  <FiCalendar size={13} className="text-amber-600" />
                  <span>Select Event</span>
                </span>
                {selectedEventObj?.status && (
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                      selectedEventObj.status.toLowerCase() === "running"
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        : "bg-gray-100 text-gray-600 border border-gray-200"
                    }`}
                  >
                    {selectedEventObj.status}
                  </span>
                )}
              </div>
              <div className="mt-1.5">
                <select
                  value={selectedEventId}
                  onChange={(e) => handleEventChange(e.target.value)}
                  className="w-full bg-white hover:bg-gray-50 border border-gray-300 text-gray-900 font-bold text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#ffff00] cursor-pointer shadow-2xs"
                >
                  {activeRunningEvent && (
                    <option value={activeRunningEvent.eventId || activeRunningEvent.id}>
                      🟢 {activeRunningEvent.eventName || `Event #${activeRunningEvent.id}`}
                    </option>
                  )}
                  <option value="all">🏆 All Events (Global)</option>
                  {(Array.isArray(eventsList) ? eventsList : [])
                    .filter(
                      (ev) =>
                        !activeRunningEvent ||
                        String(ev.eventId || ev.id) !==
                          String(activeRunningEvent.eventId || activeRunningEvent.id)
                    )
                    .map((ev) => {
                      const isCompleted = ev.status?.toLowerCase() === "completed";
                      return (
                        <option key={ev.eventId || ev.id} value={ev.eventId || ev.id}>
                          {isCompleted ? "⚪ " : "🟡 "}
                          {ev.eventName || `Event #${ev.id}`}
                        </option>
                      );
                    })}
                </select>
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium truncate">
                Filter metrics &amp; charts
              </p>
            </div>

            {/* 2. Today Total Session */}
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-4 flex flex-col justify-between hover:border-gray-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Today Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FiClock size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-gray-900 font-mono mt-1.5">
                {(stats?.todaySessions ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium">
                Sessions played today (UTC)
              </p>
            </div>

            {/* 3. Today's Awarded Points */}
            <div className="bg-white rounded-2xl shadow-xs border border-emerald-200/80 bg-gradient-to-br from-white to-emerald-50/20 p-4 flex flex-col justify-between hover:border-emerald-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                  Today's Awarded Points
                </span>
                <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                  <FiZap size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-emerald-700 font-mono mt-1.5">
                +{ (stats?.todayPoints ?? 0).toLocaleString() }{" "}
                <span className="text-[11px] font-sans text-emerald-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-emerald-700/80 mt-1.5 font-medium">
                Claimed by players today
              </p>
            </div>

            {/* 4. Event's Total Points */}
            <div className="bg-white rounded-2xl shadow-xs border border-purple-200/80 bg-gradient-to-br from-white to-purple-50/20 p-4 flex flex-col justify-between hover:border-purple-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-purple-800 uppercase tracking-wider">
                  Event's Total Points
                </span>
                <div className="w-6 h-6 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                  <FiAward size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-purple-700 font-mono mt-1.5">
                {(stats?.eventTotalPoints ?? 0).toLocaleString()}{" "}
                <span className="text-[11px] font-sans text-purple-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-purple-700/80 mt-1.5 font-medium truncate">
                Cumulative in {selectedEventName}
              </p>
            </div>

            {/* 5. Event's Total Sessions */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">
                  Event's Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
                  <FiLayers size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-amber-700 font-mono mt-1.5">
                {(stats?.eventTotalSessions ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-amber-700/80 mt-1.5 font-medium truncate">
                All rounds in {selectedEventName}
              </p>
            </div>
          </div>

          {/* Dual Metric Interactive Charts: Session Volume & Points Economy */}
          <GameAnalyticsCharts
            dailyStats={dailyStats}
            historyList={analyticsHistory}
            selectedEvent={selectedEventObj}
            selectedEventId={selectedEventId}
            themeColor="purple"
          />

          {/* Sub Navigation Tabs */}
          <div className="flex gap-2 bg-white border border-gray-200 rounded-xl p-1.5 w-fit shadow-xs">
            <button
              type="button"
              onClick={() => setActiveTab("settings")}
              className={`px-5 py-2 text-xs font-bold rounded-lg transition flex items-center gap-2 cursor-pointer ${activeTab === "settings"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
            >
              <FiSettings size={14} />
              <span>Game Settings</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`px-5 py-2 text-xs font-bold rounded-lg transition flex items-center gap-2 cursor-pointer ${activeTab === "history"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
            >
              <FiList size={14} />
              <span>Game History & Logs</span>
            </button>
          </div>

          {/* TAB 1: GAME SETTINGS */}
          {activeTab === "settings" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-7">
              {settingsLoading ? (
                <div className="py-16 flex flex-col items-center justify-center gap-3 text-gray-400">
                  <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs font-semibold text-gray-500">Loading Boom Cards settings...</p>
                </div>
              ) : (
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  {/* Status Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div>
                      <label className="text-sm font-bold text-gray-800">
                        Boom Cards Game Status
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Enable or disable the Boom Cards mini-game in the mobile app.
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-bold ${formSettings.isActive ? "text-emerald-700" : "text-gray-500"}`}>
                        {formSettings.isActive ? "Active (Enabled)" : "Disabled"}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formSettings.isActive}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, isActive: e.target.checked }))
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffff00]"></div>
                      </label>
                    </div>
                  </div>

                  {/* 4 Core Fields Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Game Time */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Game Duration (Seconds)
                      </label>
                      <input
                        type="number"
                        min={5}
                        max={600}
                        value={formSettings.gameTime}
                        onChange={(e) =>
                          setFormSettings((prev) => ({ ...prev, gameTime: e.target.value }))
                        }
                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
                        placeholder="e.g. 30"
                      />
                      <span className="text-[11px] text-gray-400 mt-1 block">
                        Default: 30 seconds per match session
                      </span>
                    </div>

                    {/* Cooldown Time */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Break / Cooldown Time (Seconds)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={86400}
                        value={formSettings.cooldownSeconds}
                        onChange={(e) =>
                          setFormSettings((prev) => ({ ...prev, cooldownSeconds: e.target.value }))
                        }
                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
                        placeholder="e.g. 180 (3 mins)"
                      />
                      <span className="text-[11px] text-gray-400 mt-1 block">
                        180s = 3 minutes cooldown between games
                      </span>
                    </div>

                    {/* Min Points */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Minimum Reward Points
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={formSettings.minPoints}
                        onChange={(e) =>
                          setFormSettings((prev) => ({ ...prev, minPoints: e.target.value }))
                        }
                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
                        placeholder="e.g. 10"
                      />
                      <span className="text-[11px] text-gray-400 mt-1 block">
                        Minimum score / points rewarded
                      </span>
                    </div>

                    {/* Max Points */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Maximum Reward Points
                      </label>
                      <input
                        type="number"
                        min={Number(formSettings.minPoints) + 1}
                        value={formSettings.maxPoints}
                        onChange={(e) =>
                          setFormSettings((prev) => ({ ...prev, maxPoints: e.target.value }))
                        }
                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
                        placeholder="e.g. 50"
                      />
                      <span className="text-[11px] text-gray-400 mt-1 block">
                        Maximum score / points rewarded
                      </span>
                    </div>
                  </div>

                  {/* Target Point Probability Buckets Section */}
                  <div className="bg-amber-50/40 border border-amber-200/80 rounded-2xl p-5 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-black text-gray-900 flex items-center gap-1.5">
                            <FiTarget className="h-4 w-4 text-amber-700" />
                            <span>Target Point Probability Buckets</span>
                          </h3>
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 ${pointProbabilities.reduce((acc, b) => acc + (Number(b.percentage) || 0), 0) === 100
                                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                                : "bg-amber-100 text-amber-900 border-amber-300"
                              }`}
                          >
                            {pointProbabilities.reduce((acc, b) => acc + (Number(b.percentage) || 0), 0) === 100 ? (
                              <>
                                <FiCheckCircle className="h-3 w-3" />
                                <span>100% Balanced</span>
                              </>
                            ) : (
                              <>
                                <FiAlertTriangle className="h-3 w-3" />
                                <span>
                                  Sum: {pointProbabilities.reduce((acc, b) => acc + (Number(b.percentage) || 0), 0)}% (Requires 100%)
                                </span>
                              </>
                            )}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Set probability chances (%) for random target reward intervals (e.g. 10-20 pts: 50%).
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleAutoBalance}
                          className="px-2.5 py-1 bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-bold transition shadow-2xs cursor-pointer flex items-center gap-1"
                          title="Evenly distribute remaining % across buckets"
                        >
                          <span>⚡</span> Auto-Balance
                        </button>
                        <button
                          type="button"
                          onClick={handleResetBuckets}
                          className="px-2.5 py-1 bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-bold transition shadow-2xs cursor-pointer flex items-center gap-1"
                          title="Reset to recommended distribution"
                        >
                          <FiRefreshCw className="h-3 w-3" /> Defaults
                        </button>
                      </div>
                    </div>

                    {/* Proportional Colored Distribution Bar */}
                    <div className="space-y-1.5">
                      <div className="w-full h-3.5 rounded-full bg-gray-200 overflow-hidden flex shadow-inner">
                        {pointProbabilities.map((b, idx) => {
                          const colors = ["bg-amber-500", "bg-purple-500", "bg-emerald-500", "bg-blue-500", "bg-rose-500", "bg-indigo-500", "bg-teal-500"];
                          const color = colors[idx % colors.length];
                          const pct = Math.max(0, Math.min(100, Number(b.percentage) || 0));
                          if (pct <= 0) return null;
                          return (
                            <div
                              key={idx}
                              style={{ width: `${pct}%` }}
                              className={`${color} h-full transition-all duration-300 relative group`}
                              title={`Bucket #${idx + 1} (${b.min}-${b.max} pts): ${pct}%`}
                            />
                          );
                        })}
                      </div>

                      {/* Legend */}
                      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-600 font-medium pt-1">
                        {pointProbabilities.map((b, idx) => {
                          const dotColors = ["bg-amber-500", "bg-purple-500", "bg-emerald-500", "bg-blue-500", "bg-rose-500", "bg-indigo-500", "bg-teal-500"];
                          return (
                            <div key={idx} className="flex items-center gap-1.5">
                              <span className={`w-2.5 h-2.5 rounded-full ${dotColors[idx % dotColors.length]}`}></span>
                              <span>
                                {b.min} - {b.max} pts: <strong>{b.percentage || 0}%</strong>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Bucket Rows */}
                    <div className="space-y-2.5 pt-1">
                      {pointProbabilities.map((bucket, index) => {
                        const borderColors = ["border-l-amber-500", "border-l-purple-500", "border-l-emerald-500", "border-l-blue-500", "border-l-rose-500", "border-l-indigo-500"];
                        return (
                          <div
                            key={index}
                            className={`bg-white border border-gray-200 border-l-4 ${borderColors[index % borderColors.length]} rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-lg bg-gray-100 font-mono font-bold text-gray-700 text-xs flex items-center justify-center">
                                #{index + 1}
                              </span>
                              <span className="text-xs font-bold text-gray-800">Interval Bucket</span>
                            </div>

                            <div className="grid grid-cols-3 sm:flex sm:items-center gap-2 flex-1 sm:justify-end">
                              {/* Min */}
                              <div className="flex flex-col">
                                <label className="text-[10px] text-gray-400 font-bold uppercase">Min PTS</label>
                                <input
                                  type="number"
                                  min={1}
                                  value={bucket.min}
                                  onChange={(e) => handleBucketChange(index, "min", e.target.value)}
                                  className="w-full sm:w-20 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-900 focus:outline-none focus:ring-1 focus:ring-amber-400"
                                />
                              </div>

                              {/* Max */}
                              <div className="flex flex-col">
                                <label className="text-[10px] text-gray-400 font-bold uppercase">Max PTS</label>
                                <input
                                  type="number"
                                  min={Number(bucket.min)}
                                  value={bucket.max}
                                  onChange={(e) => handleBucketChange(index, "max", e.target.value)}
                                  className="w-full sm:w-20 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-900 focus:outline-none focus:ring-1 focus:ring-amber-400"
                                />
                              </div>

                              {/* Chance % */}
                              <div className="flex flex-col">
                                <label className="text-[10px] text-gray-400 font-bold uppercase">Chance (%)</label>
                                <div className="relative">
                                  <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={bucket.percentage}
                                    onChange={(e) => handleBucketChange(index, "percentage", e.target.value)}
                                    className="w-full sm:w-24 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-amber-400 pr-6"
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                                </div>
                              </div>

                              {/* Delete Button */}
                              <div className="col-span-3 sm:col-span-1 flex items-end justify-end pt-2 sm:pt-4">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveBucket(index)}
                                  disabled={pointProbabilities.length <= 1}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Remove Bucket"
                                >
                                  <FiTrash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Add Bucket Button */}
                    <div className="pt-1">
                      <button
                        type="button"
                        onClick={handleAddBucket}
                        className="w-full sm:w-auto px-4 py-2 bg-white hover:bg-gray-100 text-gray-800 border border-dashed border-gray-300 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                      >
                        <FiPlus size={14} />
                        <span>Add Range Bucket</span>
                      </button>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="pt-2 border-t border-gray-100 flex justify-end">
                    <button
                      type="submit"
                      disabled={savingSettings}
                      className="px-6 py-2.5 bg-[#ffff00] hover:bg-[#e6e600] text-black font-bold text-xs rounded-xl shadow-xs transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      <FiSave size={14} />
                      {savingSettings ? "Saving..." : "Save Boom Cards Settings"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* TAB 2: SEPARATE HISTORY TABLE WITH PAGINATION */}
          {activeTab === "history" && (
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 overflow-hidden space-y-4">
              {/* Filter Toolbar */}
              <div className="p-4 sm:p-5 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="relative min-w-[260px] flex-1 max-w-md">
                  <FiSearch
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder="Search by Player, User ID, or Session ID..."
                    className="w-full pl-8 pr-7 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                  />
                  {search && (
                    <button
                      onClick={() => {
                        setSearch("");
                        setDebouncedSearch("");
                        setPage(1);
                      }}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <FiX size={13} />
                    </button>
                  )}
                </div>

                {/* Status Filter */}
                <div className="inline-flex bg-gray-100 rounded-xl p-1 border border-gray-200 text-xs font-semibold">
                  {[
                    { key: "all", label: "All" },
                    { key: "completed", label: "Completed" },
                    { key: "disqualified", label: "Disqualified" },
                    { key: "started", label: "Started" },
                  ].map((item) => (
                    <button
                      key={item.key}
                      onClick={() => {
                        setStatusFilter(item.key);
                        setPage(1);
                      }}
                      className={`px-3 py-1 rounded-lg transition cursor-pointer ${statusFilter === item.key
                          ? "bg-black text-[#ffff00] font-bold shadow-xs"
                          : "text-gray-600 hover:text-gray-900"
                        }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Table Body */}
              {historyLoading ? (
                <div className="py-20 flex flex-col items-center justify-center gap-3 text-gray-400">
                  <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs font-semibold text-gray-500">Loading Boom Cards history records...</p>
                </div>
              ) : history.length === 0 ? (
                <div className="py-20 text-center text-gray-400 flex flex-col items-center justify-center gap-2">
                  <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center text-2xl font-bold">
                    🃏
                  </div>
                  <p className="text-sm font-bold text-gray-700">No Boom Cards records found</p>
                  <p className="text-xs text-gray-400">No activity logged for the selected filters.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                        <th className="px-5 py-3.5">Player</th>
                        <th className="px-5 py-3.5">Session ID</th>
                        <th className="px-5 py-3.5 text-center">Score / Flips</th>
                        <th className="px-5 py-3.5 text-center">Points Awarded</th>
                        <th className="px-5 py-3.5 text-center">Status</th>
                        <th className="px-5 py-3.5">Date & Time</th>
                        <th className="px-5 py-3.5 text-center">Info</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {history.map((row, idx) => {
                        const scoreVal = Number(row.score ?? row.taps ?? row.matchedPairs ?? 0);
                        const pointsAwarded = Number(row.pointsAwarded ?? row.points ?? row.claimedPoints ?? row.targetPoints ?? 0);
                        const userInitial = (row.username || "U")[0].toUpperCase();

                        return (
                          <tr
                            key={row.id || row.sessionId || idx}
                            className="hover:bg-gray-50/70 transition-colors"
                          >
                            {/* Player */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                  {userInitial}
                                </div>
                                <div>
                                  <Link
                                    to={`/user/${row.userId}`}
                                    className="font-bold text-gray-900 hover:underline block truncate max-w-[140px]"
                                  >
                                    {row.username || "Unknown Miner"}
                                  </Link>
                                  <span className="text-[10px] text-gray-400 font-mono">
                                    #{row.userId}
                                  </span>
                                </div>
                              </div>
                            </td>

                            {/* Session ID */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <div className="flex items-center gap-1.5 font-mono text-[11px] text-gray-600 bg-gray-50 border border-gray-200 px-2 py-1 rounded-md w-fit">
                                <span className="truncate max-w-[110px]">
                                  {row.sessionId || "—"}
                                </span>
                                {row.sessionId && (
                                  <button
                                    type="button"
                                    onClick={() => handleCopySession(row.sessionId)}
                                    className="text-gray-400 hover:text-black cursor-pointer"
                                    title="Copy Session ID"
                                  >
                                    {copiedSessionId === row.sessionId ? (
                                      <FiCheck size={11} className="text-emerald-600" />
                                    ) : (
                                      <FiCopy size={11} />
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>

                            {/* Score / Flips */}
                            <td className="px-5 py-3.5 text-center whitespace-nowrap font-mono font-bold text-gray-700">
                              {scoreVal > 0 ? scoreVal.toLocaleString() : "—"}
                            </td>

                            {/* Points Awarded */}
                            <td className="px-5 py-3.5 text-center whitespace-nowrap">
                              <span className="inline-flex items-center gap-1 font-black text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-1 rounded-lg">
                                <span>🎯</span> {pointsAwarded.toLocaleString()} PTS
                              </span>
                            </td>

                            {/* Status */}
                            <td className="px-5 py-3.5 text-center whitespace-nowrap">
                              {row.status === "completed" || row.status === "Claimed" ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <FiCheckCircle size={11} /> Completed
                                </span>
                              ) : row.status === "disqualified" || row.status === "CheatDetected" ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                  <FiXCircle size={11} /> Disqualified
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                                  {row.status || "Started"}
                                </span>
                              )}
                            </td>

                            {/* Date & Time */}
                            <td className="px-5 py-3.5 whitespace-nowrap text-gray-500">
                              {formatDateTime(row.createdAt || row.transactionDate || row.startedAt)}
                            </td>

                            {/* Info */}
                            <td className="px-5 py-3.5 text-center whitespace-nowrap">
                              <span className="text-[11px] text-gray-400 font-mono">
                                #{row.id || "—"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="p-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <span>
                    Showing page <strong>{pagination.page}</strong> of{" "}
                    <strong>{pagination.totalPages}</strong> ({pagination.total} total sessions)
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-semibold"
                    >
                      &laquo; Prev
                    </button>
                    <button
                      type="button"
                      disabled={page === pagination.totalPages}
                      onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-semibold"
                    >
                      Next &raquo;
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
